import { request } from "../http/api-client";

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface PSPProvider {
  id: string;
  code: string;
  name: string;
  type: "PAYIN" | "PAYOUT" | "BOTH";
  status: "ACTIVE" | "INACTIVE";
  supportedMethods: string[];
  supportedCurrencies: string[];
  features: Record<string, boolean>;
  healthStatus?: "HEALTHY" | "DEGRADED" | "DOWN";
  createdAt: string;
  updatedAt: string;
}

export interface PSPConfiguration {
  id: string;
  merchantId: string;
  provider: string;
  isEnabled: boolean;
  isSandbox: boolean;
  isActive: boolean;
  credentials: Record<string, any>;
  priority: number;
  webhookUrl?: string;
  returnUrl?: string;
  features: Record<string, any>;
  paymentMethods: string[];
  currencies: string[];
  limits: {
    minAmount: number;
    maxAmount: number;
  };
  fees: {
    percentage: number;
    fixed: number;
    currency: string;
  };
  runtimeConfig?: Record<string, any>;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface PSPHealth {
  provider: string;
  status: "HEALTHY" | "DEGRADED" | "DOWN";
  responseTime: number;
  successRate: number;
  lastChecked: string;
}

export interface PSPConfigStats {
  total: number;
  enabled: number;
  disabled: number;
  sandbox: number;
  production: number;
}

export interface PSPProviderStats {
  total: number;
  active: number;
  inactive: number;
  healthy: number;
  degraded: number;
  down: number;
}

export interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PSPConfigFilters {
  page?: number;
  limit?: number;
  merchantId?: string;
  provider?: string;
  isEnabled?: boolean;
  isSandbox?: boolean;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
}

export interface PSPProviderFilters {
  page?: number;
  limit?: number;
  code?: string;
  type?: "PAYIN" | "PAYOUT" | "BOTH";
  status?: "ACTIVE" | "INACTIVE";
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
}

export interface CreatePSPConfigRequest {
  merchantId: string;
  provider: string;
  isEnabled: boolean;
  isSandbox: boolean;
  isActive: boolean;
  credentials: Record<string, any>;
  webhookUrl?: string;
  returnUrl?: string;
  features: Record<string, boolean>;
  paymentMethods: string[];
  currencies: string[];
  limits: {
    minAmount: number;
    maxAmount: number;
  };
  fees: {
    percentage: number;
    fixed: number;
    currency: string;
  };
  priority?: number;
  runtimeConfig?: Record<string, any>;
  metadata?: Record<string, any>;
}

export interface UpdatePSPConfigRequest {
  isEnabled?: boolean;
  isSandbox?: boolean;
  isActive?: boolean;
  credentials?: Record<string, any>;
  webhookUrl?: string;
  returnUrl?: string;
  features?: Record<string, boolean>;
  paymentMethods?: string[];
  currencies?: string[];
  limits?: {
    minAmount?: number;
    maxAmount?: number;
  };
  fees?: {
    percentage?: number;
    fixed?: number;
    currency?: string;
  };
  priority?: number;
  runtimeConfig?: Record<string, any>;
  metadata?: Record<string, any>;
}

// ============================================================================
// PSP Configuration APIs
// ============================================================================

/**
 * Fetch all PSP configurations with optional filters
 */
export async function fetchPSPConfigurations(
  filters: PSPConfigFilters = {}
) {
  const params = new URLSearchParams();

  if (filters.page) params.set("page", String(filters.page));
  if (filters.limit) params.set("limit", String(filters.limit));
  if (filters.merchantId) params.set("merchantId", filters.merchantId);
  if (filters.provider) params.set("provider", filters.provider);
  if (filters.isEnabled !== undefined)
    params.set("isEnabled", String(filters.isEnabled));
  if (filters.isSandbox !== undefined)
    params.set("isSandbox", String(filters.isSandbox));
  if (filters.isActive !== undefined)
    params.set("isActive", String(filters.isActive));
  if (filters.sortBy) params.set("sortBy", filters.sortBy);
  if (filters.sortOrder) params.set("sortOrder", filters.sortOrder);

  const url = `/api/v1/admin/psp/configurations${
    params.toString() ? `?${params.toString()}` : ""
  }`;

  const res = await request<{
    success: boolean;
    data: PSPConfiguration[];
    pagination: PaginationInfo;
    message: string;
  }>(url, { method: "GET" });

  return res;
}

/**
 * Fetch single PSP configuration by ID
 */
export async function fetchPSPConfiguration(id: string) {
  const url = `/api/v1/admin/psp/configurations/${id}`;
  const res = await request<{
    success: boolean;
    data: PSPConfiguration;
    message: string;
  }>(url, { method: "GET" });

  return res.data;
}

/**
 * Create new PSP configuration
 */
export async function createPSPConfiguration(data: CreatePSPConfigRequest) {
  const url = `/api/v1/admin/psp/configurations`;
  const res = await request<{
    success: boolean;
    data: PSPConfiguration;
    message: string;
  }>(url, {
    method: "POST",
    body: data,
  });

  return res.data;
}

/**
 * Update existing PSP configuration
 */
export async function updatePSPConfiguration(
  id: string,
  data: UpdatePSPConfigRequest
) {
  const url = `/api/v1/admin/psp/configurations/${id}`;
  const res = await request<{
    success: boolean;
    data: PSPConfiguration;
    message: string;
  }>(url, {
    method: "PUT",
    body: data,
  });

  return res.data;
}

/**
 * Delete PSP configuration (soft delete - sets isEnabled to false)
 */
export async function deletePSPConfiguration(id: string) {
  const url = `/api/v1/admin/psp/configurations/${id}`;
  await request(url, { method: "DELETE" });
}

/**
 * Toggle PSP configuration status (enable/disable)
 */
export async function togglePSPConfigStatus(id: string, isEnabled: boolean) {
  return await updatePSPConfiguration(id, { isEnabled });
}

/**
 * Bulk toggle PSP configurations
 */
export async function bulkTogglePSPConfigs(ids: string[], isEnabled: boolean) {
  const promises = ids.map((id) =>
    updatePSPConfiguration(id, { isEnabled })
  );
  return await Promise.all(promises);
}

/**
 * Bulk delete PSP configurations
 */
export async function bulkDeletePSPConfigs(ids: string[]) {
  const promises = ids.map((id) => deletePSPConfiguration(id));
  return await Promise.all(promises);
}

// ============================================================================
// PSP Provider APIs
// ============================================================================

/**
 * Fetch all PSP providers with optional filters
 */
export async function fetchPSPProviders(filters: PSPProviderFilters = {}) {
  const params = new URLSearchParams();

  if (filters.page) params.set("page", String(filters.page));
  if (filters.limit) params.set("limit", String(filters.limit));
  if (filters.code) params.set("code", filters.code);
  if (filters.type) params.set("type", filters.type);
  if (filters.status) params.set("status", filters.status);
  if (filters.sortBy) params.set("sortBy", filters.sortBy);
  if (filters.sortOrder) params.set("sortOrder", filters.sortOrder);

  const url = `/api/v1/admin/psp/providers${
    params.toString() ? `?${params.toString()}` : ""
  }`;

  const res = await request<{
    success: boolean;
    data: PSPProvider[];
    pagination: PaginationInfo;
    message: string;
  }>(url, { method: "GET" });

  return res;
}

/**
 * Fetch single PSP provider by ID
 */
export async function fetchPSPProvider(id: string) {
  const url = `/api/v1/admin/psp/providers/${id}`;
  const res = await request<{
    success: boolean;
    data: PSPProvider;
    message: string;
  }>(url, { method: "GET" });

  return res.data;
}

/**
 * Create new PSP provider
 */
export async function createPSPProvider(data: Partial<PSPProvider>) {
  const url = `/api/v1/admin/psp/providers`;
  const res = await request<{
    success: boolean;
    data: PSPProvider;
    message: string;
  }>(url, {
    method: "POST",
    body: data,
  });

  return res.data;
}

/**
 * Update existing PSP provider
 */
export async function updatePSPProvider(
  id: string,
  data: Partial<PSPProvider>
) {
  const url = `/api/v1/admin/psp/providers/${id}`;
  const res = await request<{
    success: boolean;
    data: PSPProvider;
    message: string;
  }>(url, {
    method: "PUT",
    body: data,
  });

  return res.data;
}

/**
 * Delete PSP provider
 */
export async function deletePSPProvider(id: string) {
  const url = `/api/v1/admin/psp/providers/${id}`;
  await request(url, { method: "DELETE" });
}

// ============================================================================
// Health & Monitoring APIs
// ============================================================================

/**
 * Fetch health status for all PSP providers
 */
export async function fetchPSPHealth() {
  const url = `/api/v1/admin/psp/health`;
  const res = await request<{
    success: boolean;
    data: PSPHealth[];
    message: string;
  }>(url, { method: "GET" });

  return res.data;
}

/**
 * Test PSP provider connection
 */
export async function testPSPConnection(provider: string) {
  const url = `/api/v1/admin/psp/${provider}/test`;
  const res = await request<{
    success: boolean;
    data: {
      connected: boolean;
      responseTime: number;
      message: string;
    };
    message: string;
  }>(url, { method: "POST" });

  return res.data;
}

/**
 * Get PSP provider capabilities
 */
export async function fetchPSPCapabilities(provider: string) {
  const url = `/api/v1/admin/psp/${provider}/capabilities`;
  const res = await request<{
    success: boolean;
    data: {
      paymentMethods: string[];
      currencies: string[];
      features: string[];
    };
    message: string;
  }>(url, { method: "GET" });

  return res.data;
}

// ============================================================================
// Statistics APIs
// ============================================================================

/**
 * Get PSP configuration statistics
 */
export async function fetchPSPConfigStats(): Promise<PSPConfigStats> {
  const res = await fetchPSPConfigurations({ limit: 100 });
  const configs = res.data || [];

  return {
    total: res.pagination?.total || 0,
    enabled: configs.filter((c) => c.isEnabled).length,
    disabled: configs.filter((c) => !c.isEnabled).length,
    sandbox: configs.filter((c) => c.isSandbox).length,
    production: configs.filter((c) => !c.isSandbox).length,
  };
}

/**
 * Get PSP provider statistics
 */
export async function fetchPSPProviderStats(): Promise<PSPProviderStats> {
  const res = await fetchPSPProviders({ limit: 100 });
  const providers = res.data || [];
  const health = await fetchPSPHealth();

  const healthMap = new Map((health || []).map((h) => [h.provider, h.status]));

  return {
    total: res.pagination?.total || 0,
    active: providers.filter((p) => p.status === "ACTIVE").length,
    inactive: providers.filter((p) => p.status === "INACTIVE").length,
    healthy: Array.from(healthMap.values()).filter((s) => s === "HEALTHY")
      .length,
    degraded: Array.from(healthMap.values()).filter((s) => s === "DEGRADED")
      .length,
    down: Array.from(healthMap.values()).filter((s) => s === "DOWN").length,
  };
}
